import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Calendar, Clock, MapPin, Users, Plus, Edit2, Trash2, Eye, X, Upload, Link as LinkIcon, Film, FileText, Image as ImageIcon, ChevronDown, ChevronUp, Play, ExternalLink, File } from 'lucide-react'
import api from '../services/api'
import Card from '../components/Card'
import Button from '../components/Button'
import LoadingSpinner from '../components/LoadingSpinner'
import { getToken, clearAuth } from '../utils/userData'
import { toLocalDatetimeString } from '../utils/helpers'
import { 
  getMediaUrl, 
  detectMediaType, 
  validateMediaFile, 
  validateMediaUrl, 
  getAcceptedMediaTypes,
  getMediaTypeIcon,
  getMediaDisplayName,
  formatFileSize
} from '../utils/mediaHelpers'

// Event format options
const FORMAT_OPTIONS = [
  { value: 'in-person', label: 'In-Person' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
]

// Event status options
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'cancelled', label: 'Cancelled' },
]

// Initial form state
const INITIAL_FORM = {
  name: '',
  eventDate: '',
  endDate: '',
  format: 'in-person',
  spaceId: '',
  location: '',
  venueLink: '',
  shortDescription: '',
  detailedDescription: '',
  fee: '',
  registrationLink: '',
  registrationOpen: true,
  maxAttendees: '',
  status: 'published',
  facilitatorIds: [],
  externalFacilitators: [],
}

const parseNotes = (notes) => {
  if (!notes) return null
  try {
    return JSON.parse(notes)
  } catch {
    return null
  }
}

const RegistrationRow = ({ reg, index }) => {
  const [expanded, setExpanded] = useState(false)
  const formData = parseNotes(reg.notes)
  const hasFormData =
    formData &&
    (formData.jobTitle ||
      formData.organization ||
      formData.primaryGoals?.length ||
      formData.heardAbout ||
      formData.phone ||
      formData.transactionId ||
      formData.age ||
      formData.gender ||
      formData.company ||
      formData.sessionSelection ||
      formData.facilitatorQueries)

  return (
    <div className="p-4 bg-gray-50 rounded-xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold flex-shrink-0">
            {index + 1}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900">{reg.user.name}</p>
              {reg.isGuest && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
                  Guest
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{reg.user.email}</p>
            {reg.user.phone && (
              <p className="text-sm text-gray-500">{reg.user.phone}</p>
            )}
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
          reg.status === 'registered' ? 'bg-green-100 text-green-800' :
          reg.status === 'attended' ? 'bg-blue-100 text-blue-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {reg.status}
        </span>
      </div>
      {hasFormData && (
        <div className="mt-2 ml-14">
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 transition-colors"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Hide form responses' : 'View form responses'}
          </button>
          {expanded && (
            <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200 space-y-1.5 text-xs">
              {formData.phone && (
                <div><span className="text-gray-400">Phone:</span> <span className="text-gray-700">{formData.phone}</span></div>
              )}
              {formData.jobTitle && (
                <div><span className="text-gray-400">Job Title:</span> <span className="text-gray-700">{formData.jobTitle}</span></div>
              )}
              {formData.organization && (
                <div><span className="text-gray-400">Organization:</span> <span className="text-gray-700">{formData.organization}</span></div>
              )}
              {formData.primaryGoals?.length > 0 && (
                <div>
                  <span className="text-gray-400">Goals:</span>
                  <ul className="mt-0.5 ml-3 space-y-0.5">
                    {formData.primaryGoals.map((goal, i) => (
                      <li key={i} className="text-gray-700">- {goal}</li>
                    ))}
                  </ul>
                </div>
              )}
              {formData.heardAbout && (
                <div><span className="text-gray-400">Heard about us:</span> <span className="text-gray-700">{formData.heardAbout}</span></div>
              )}
              {formData.transactionId && (
                <div><span className="text-gray-400">Transaction ID:</span> <span className="text-gray-700">{formData.transactionId}</span></div>
              )}
              {formData.age && (
                <div><span className="text-gray-400">Age:</span> <span className="text-gray-700">{formData.age}</span></div>
              )}
              {formData.gender && (
                <div><span className="text-gray-400">Gender:</span> <span className="text-gray-700">{formData.gender}</span></div>
              )}
              {formData.company && (
                <div><span className="text-gray-400">Company:</span> <span className="text-gray-700">{formData.company}</span></div>
              )}
              {formData.sessionSelection && (
                <div><span className="text-gray-400">Session selection:</span> <span className="text-gray-700">{formData.sessionSelection}</span></div>
              )}
              {formData.facilitatorQueries && (
                <div><span className="text-gray-400">Queries:</span> <span className="text-gray-700 whitespace-pre-wrap">{formData.facilitatorQueries}</span></div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const FacilitatorEvents = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [events, setEvents] = useState({ upcoming: [], past: [] })
  const [activeTab, setActiveTab] = useState('upcoming')
  const [isSpaceOwner, setIsSpaceOwner] = useState(false)
  const [spaces, setSpaces] = useState([])
  const [facilitators, setFacilitators] = useState([])

  // Modal states
  const [showEventModal, setShowEventModal] = useState(false)
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [eventForm, setEventForm] = useState(INITIAL_FORM)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  
  // Registrations state
  const [registrations, setRegistrations] = useState([])
  const [registrationsLoading, setRegistrationsLoading] = useState(false)
  const [selectedEventForRegistrations, setSelectedEventForRegistrations] = useState(null)

  // External facilitator input
  const [externalFacInput, setExternalFacInput] = useState('')

  // Media state
  const [existingMedia, setExistingMedia] = useState([]) // Media already saved for the event
  const [newMediaFiles, setNewMediaFiles] = useState([]) // New files to upload
  const [newMediaUrls, setNewMediaUrls] = useState([]) // New URLs to add
  const [mediaUrlInput, setMediaUrlInput] = useState('')
  const [mediaError, setMediaError] = useState('')
  const [deletedMediaIds, setDeletedMediaIds] = useState([]) // IDs of media to delete on save

  // Post-event resources state
  const [postEventResources, setPostEventResources] = useState([])
  const [resourceForm, setResourceForm] = useState({ title: '', description: '', url: '', resourceType: 'recording', mode: 'link' })
  const [resourceFile, setResourceFile] = useState(null)
  const [resourceError, setResourceError] = useState('')
  const [resourceSaving, setResourceSaving] = useState(false)

  useEffect(() => {
    const token = getToken()
    if (!token) {
      navigate('/')
      return
    }
    fetchData()
  }, [navigate])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [eventsRes, spaceOwnerRes, spacesRes, facilitatorsRes] = await Promise.all([
        api.get('/api/facilitators/events'),
        api.get('/api/facilitators/is-space-owner'),
        api.get('/api/facilitators/spaces-list'),
        api.get('/api/facilitators/facilitators-list'),
      ])

      setEvents(eventsRes.data.data)
      setIsSpaceOwner(spaceOwnerRes.data.data.isSpaceOwner)
      setSpaces(spacesRes.data.data)
      setFacilitators(facilitatorsRes.data.data)
    } catch (err) {
      console.error('Error fetching data:', err)
      if (err.response?.status === 401) {
        clearAuth()
        navigate('/')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleOpenCreateModal = () => {
    setEditingEvent(null)
    setEventForm(INITIAL_FORM)
    setImageFile(null)
    setImagePreview(null)
    setFormError('')
    // Reset media state
    setExistingMedia([])
    setNewMediaFiles([])
    setNewMediaUrls([])
    setMediaUrlInput('')
    setMediaError('')
    setDeletedMediaIds([])
    // Reset resource state
    setPostEventResources([])
    setResourceForm({ title: '', description: '', url: '', resourceType: 'recording', mode: 'link' })
    setResourceFile(null)
    setResourceError('')
    setShowEventModal(true)
  }

  const handleOpenEditModal = (event) => {
    setEditingEvent(event)
    setEventForm({
      name: event.name,
      eventDate: toLocalDatetimeString(event.eventDate),
      endDate: toLocalDatetimeString(event.endDate),
      format: event.format,
      spaceId: event.spaceId || '',
      location: event.location || '',
      venueLink: event.venueLink || '',
      shortDescription: event.shortDescription,
      detailedDescription: event.detailedDescription,
      fee: event.fee || '',
      registrationLink: event.registrationLink || '',
      registrationOpen: event.registrationOpen,
      maxAttendees: event.maxAttendees || '',
      status: event.status,
      facilitatorIds: event.facilitators?.map(f => f.id) || [],
      externalFacilitators: event.externalFacilitators || [],
    })
    setImageFile(null)
    setImagePreview(event.image ? `${import.meta.env.VITE_API_URL || ''}${event.image}` : null)
    setFormError('')
    // Load existing media
    setExistingMedia(event.media || [])
    setNewMediaFiles([])
    setNewMediaUrls([])
    setMediaUrlInput('')
    setMediaError('')
    setDeletedMediaIds([])
    // Load existing post-event resources
    setPostEventResources(event.postEventResources || [])
    setResourceForm({ title: '', description: '', url: '', resourceType: 'recording', mode: 'link' })
    setResourceFile(null)
    setResourceError('')
    setShowEventModal(true)
  }

  const handleCloseModal = () => {
    setShowEventModal(false)
    setEditingEvent(null)
    setEventForm(INITIAL_FORM)
    setImageFile(null)
    setImagePreview(null)
    setFormError('')
    // Reset media state
    setExistingMedia([])
    setNewMediaFiles([])
    setNewMediaUrls([])
    setMediaUrlInput('')
    setMediaError('')
    setDeletedMediaIds([])
    // Reset resource state
    setPostEventResources([])
    setResourceForm({ title: '', description: '', url: '', resourceType: 'recording', mode: 'link' })
    setResourceFile(null)
    setResourceError('')
  }

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    setEventForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    setFormError('')
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImageFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleFacilitatorToggle = (profileId) => {
    setEventForm(prev => ({
      ...prev,
      facilitatorIds: prev.facilitatorIds.includes(profileId)
        ? prev.facilitatorIds.filter(id => id !== profileId)
        : [...prev.facilitatorIds, profileId]
    }))
  }

  const handleAddExternalFacilitator = () => {
    if (externalFacInput.trim()) {
      setEventForm(prev => ({
        ...prev,
        externalFacilitators: [...prev.externalFacilitators, externalFacInput.trim()]
      }))
      setExternalFacInput('')
    }
  }

  const handleRemoveExternalFacilitator = (index) => {
    setEventForm(prev => ({
      ...prev,
      externalFacilitators: prev.externalFacilitators.filter((_, i) => i !== index)
    }))
  }

  // Media handling functions
  const handleMediaFilesChange = (e) => {
    const files = Array.from(e.target.files)
    setMediaError('')
    
    const validFiles = []
    for (const file of files) {
      const validation = validateMediaFile(file)
      if (!validation.valid) {
        setMediaError(validation.error)
        continue
      }
      // Create preview URL for the file
      validFiles.push({
        file,
        type: file.type.startsWith('image/') ? 'image' : 
              file.type.startsWith('video/') ? 'video' : 'pdf',
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        name: file.name,
        size: file.size,
      })
    }
    
    setNewMediaFiles(prev => [...prev, ...validFiles])
    // Reset the input
    e.target.value = ''
  }

  const handleAddMediaUrl = () => {
    setMediaError('')
    const validation = validateMediaUrl(mediaUrlInput)
    
    if (!validation.valid) {
      setMediaError(validation.error)
      return
    }
    
    setNewMediaUrls(prev => [...prev, {
      url: mediaUrlInput.trim(),
      type: validation.type,
    }])
    setMediaUrlInput('')
  }

  const handleRemoveNewMediaFile = (index) => {
    setNewMediaFiles(prev => {
      const updated = [...prev]
      // Revoke the object URL to prevent memory leaks
      if (updated[index].preview) {
        URL.revokeObjectURL(updated[index].preview)
      }
      updated.splice(index, 1)
      return updated
    })
  }

  const handleRemoveNewMediaUrl = (index) => {
    setNewMediaUrls(prev => prev.filter((_, i) => i !== index))
  }

  const handleMarkExistingMediaForDeletion = (mediaId) => {
    setDeletedMediaIds(prev => [...prev, mediaId])
    setExistingMedia(prev => prev.filter(m => m.id !== mediaId))
  }

  const uploadNewMedia = async (eventId) => {
    // Upload files
    if (newMediaFiles.length > 0) {
      const formData = new FormData()
      newMediaFiles.forEach(item => {
        formData.append('media', item.file)
      })
      
      try {
        await api.post(`/api/facilitators/events/${eventId}/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } catch (err) {
        console.error('Error uploading media files:', err)
      }
    }

    // Add URLs
    for (const urlItem of newMediaUrls) {
      try {
        await api.post(`/api/facilitators/events/${eventId}/media`, {
          url: urlItem.url,
        })
      } catch (err) {
        console.error('Error adding media URL:', err)
      }
    }

    // Delete marked media
    for (const mediaId of deletedMediaIds) {
      try {
        await api.delete(`/api/facilitators/events/${eventId}/media/${mediaId}`)
      } catch (err) {
        console.error('Error deleting media:', err)
      }
    }
  }

  // Post-event resource handlers
  const handleAddResource = async () => {
    if (!editingEvent?.id) return
    setResourceError('')
    setResourceSaving(true)

    try {
      if (resourceForm.mode === 'file' && resourceFile) {
        const formData = new FormData()
        formData.append('file', resourceFile)
        formData.append('title', resourceForm.title || resourceFile.name)
        if (resourceForm.description) formData.append('description', resourceForm.description)
        if (resourceForm.resourceType) formData.append('resourceType', resourceForm.resourceType)

        const res = await api.post(`/api/facilitators/events/${editingEvent.id}/post-event-resources`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        setPostEventResources(prev => [...prev, res.data.data])
      } else if (resourceForm.mode === 'link' && resourceForm.url.trim()) {
        if (!resourceForm.title.trim()) {
          setResourceError('Title is required')
          setResourceSaving(false)
          return
        }
        const res = await api.post(`/api/facilitators/events/${editingEvent.id}/post-event-resources`, {
          title: resourceForm.title,
          description: resourceForm.description || null,
          url: resourceForm.url,
          resourceType: resourceForm.resourceType,
        })
        setPostEventResources(prev => [...prev, res.data.data])
      } else {
        setResourceError(resourceForm.mode === 'file' ? 'Please select a file' : 'Please enter a URL')
        setResourceSaving(false)
        return
      }
      setResourceForm({ title: '', description: '', url: '', resourceType: 'recording', mode: resourceForm.mode })
      setResourceFile(null)
    } catch (err) {
      setResourceError(err.response?.data?.message || 'Failed to add resource')
    } finally {
      setResourceSaving(false)
    }
  }

  const handleDeleteResource = async (resourceId) => {
    if (!editingEvent?.id) return
    try {
      await api.delete(`/api/facilitators/events/${editingEvent.id}/post-event-resources/${resourceId}`)
      setPostEventResources(prev => prev.filter(r => r.id !== resourceId))
    } catch (err) {
      console.error('Error deleting resource:', err)
    }
  }

  const getResourceIcon = (resourceType) => {
    switch (resourceType) {
      case 'recording': return <Play className="w-4 h-4" />
      case 'pdf': return <FileText className="w-4 h-4" />
      case 'document': return <File className="w-4 h-4" />
      default: return <ExternalLink className="w-4 h-4" />
    }
  }

  const getResourceBadgeColor = (resourceType) => {
    switch (resourceType) {
      case 'recording': return 'bg-red-100 text-red-600'
      case 'pdf': return 'bg-orange-100 text-orange-600'
      case 'document': return 'bg-blue-100 text-blue-600'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError('')
    setSubmitting(true)

    try {
      const formData = new FormData()
      
      // Add all form fields
      formData.append('name', eventForm.name)
      formData.append('eventDate', new Date(eventForm.eventDate).toISOString())
      if (eventForm.endDate) formData.append('endDate', new Date(eventForm.endDate).toISOString())
      formData.append('format', eventForm.format)
      if (eventForm.spaceId) formData.append('spaceId', eventForm.spaceId)
      if (eventForm.location) formData.append('location', eventForm.location)
      if (eventForm.venueLink) formData.append('venueLink', eventForm.venueLink)
      formData.append('shortDescription', eventForm.shortDescription)
      formData.append('detailedDescription', eventForm.detailedDescription)
      if (eventForm.fee) formData.append('fee', eventForm.fee)
      formData.append('registrationLink', (eventForm.registrationLink || '').trim())
      formData.append('registrationOpen', eventForm.registrationOpen)
      if (eventForm.maxAttendees) formData.append('maxAttendees', eventForm.maxAttendees)
      formData.append('status', eventForm.status)
      
      // Add facilitators (only if space owner)
      if (isSpaceOwner && eventForm.facilitatorIds.length > 0) {
        formData.append('facilitatorIds', JSON.stringify(eventForm.facilitatorIds))
      }
      
      // Add external facilitators
      if (eventForm.externalFacilitators.length > 0) {
        formData.append('externalFacilitators', JSON.stringify(eventForm.externalFacilitators))
      }

      // Add image if selected
      if (imageFile) {
        formData.append('image', imageFile)
      }

      let eventId = editingEvent?.id

      if (editingEvent) {
        await api.put(`/api/facilitators/events/${editingEvent.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } else {
        const response = await api.post('/api/facilitators/events', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        eventId = response.data.data.id
      }

      // Upload new media and delete marked media
      if (eventId && (newMediaFiles.length > 0 || newMediaUrls.length > 0 || deletedMediaIds.length > 0)) {
        await uploadNewMedia(eventId)
      }

      handleCloseModal()
      fetchData()
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save event')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return

    try {
      await api.delete(`/api/facilitators/events/${eventId}`)
      fetchData()
    } catch (err) {
      console.error('Error deleting event:', err)
      alert(err.response?.data?.message || 'Failed to delete event')
    }
  }

  const handleViewRegistrations = async (event) => {
    setSelectedEventForRegistrations(event)
    setRegistrationsLoading(true)
    setShowRegistrationsModal(true)

    try {
      const res = await api.get(`/api/facilitators/events/${event.id}/registrations`)
      setRegistrations(res.data.data)
    } catch (err) {
      console.error('Error fetching registrations:', err)
    } finally {
      setRegistrationsLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    }
    return colors[status] || colors.draft
  }

  const getFormatBadge = (format) => {
    const colors = {
      'in-person': 'bg-emerald-100 text-emerald-800',
      'online': 'bg-blue-100 text-blue-800',
      'hybrid': 'bg-purple-100 text-purple-800',
    }
    return colors[format] || colors['in-person']
  }

  const getEventImage = (event) => {
    if (event.image) {
      if (event.image.startsWith('http')) return event.image
      return `${import.meta.env.VITE_API_URL || ''}${event.image}`
    }
    if (event.media && event.media.length > 0) {
      const firstImageMedia = event.media.find(m => m.type === 'image')
      if (firstImageMedia) {
        return getMediaUrl(firstImageMedia.url)
      }
    }
    return `${import.meta.env.VITE_API_URL}/uploads/events/default-event.jpg`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const currentEvents = activeTab === 'upcoming' ? events.upcoming : events.past

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">My Events</h1>
              <p className="text-gray-600">Manage your wellness events and sessions</p>
            </div>
            <Button onClick={handleOpenCreateModal} className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Create Event
            </Button>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'upcoming'
                ? 'bg-primary-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Upcoming ({events.upcoming.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'past'
                ? 'bg-primary-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Past ({events.past.length})
          </button>
        </div>

        {/* Events Grid */}
        {currentEvents.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No {activeTab} events
            </h3>
            <p className="text-gray-600 mb-6">
              {activeTab === 'upcoming' 
                ? "You don't have any upcoming events. Create your first event!"
                : "You don't have any past events yet."}
            </p>
            {activeTab === 'upcoming' && (
              <Button onClick={handleOpenCreateModal}>Create Your First Event</Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 lg:gap-10 gap-4">
            {currentEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white overflow-hidden p-0">
                  {/* Event Image */}
                  <div className="aspect-[4/5] overflow-hidden bg-gray-200">
                    <img
                      src={getEventImage(event)}
                      alt={event.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = '/uploads/events/default-event.jpg' }}
                    />
                  </div>

                  <div className="p-5">
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(event.status)}`}>
                        {event.status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getFormatBadge(event.format)}`}>
                        {event.format}
                      </span>
                    </div>

                    {/* Event Name */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                      {event.name}
                    </h3>

                    {/* Date & Time */}
                    <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(event.eventDate)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                      <Clock className="w-4 h-4" />
                      <span>
                        {formatTime(event.eventDate)}
                        {event.endDate && ` - ${formatTime(event.endDate)}`}
                      </span>
                    </div>

                    {/* Location */}
                    {(event.location || event.space) && (
                      <div className="flex items-center gap-2 text-gray-600 text-sm mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="line-clamp-1">
                          {event.space?.name || event.location}
                        </span>
                      </div>
                    )}

                    {/* Registrations */}
                    <div className="flex items-center gap-2 text-gray-600 text-sm mb-4">
                      <Users className="w-4 h-4" />
                      <span>
                        {event.registrationCount} registered
                        {event.maxAttendees && ` / ${event.maxAttendees} max`}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleOpenEditModal(event)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleViewRegistrations(event)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        Registrations
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Event Create/Edit Modal */}
      <AnimatePresence>
        {showEventModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700">
                <h2 className="text-xl font-bold text-white">
                  {editingEvent ? 'Edit Event' : 'Create New Event'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="space-y-6">
                  {/* Event Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={eventForm.name}
                      onChange={handleFormChange}
                      placeholder="e.g., Morning Yoga Flow"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date & Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        name="eventDate"
                        value={eventForm.eventDate}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        name="endDate"
                        value={eventForm.endDate}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Format & Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Format <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="format"
                        value={eventForm.format}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {FORMAT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="status"
                        value={eventForm.status}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Location Section */}
                  {(eventForm.format === 'in-person' || eventForm.format === 'hybrid') && (
                    <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                      <h3 className="font-semibold text-gray-900">Location</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select a Space (optional)
                        </label>
                        <select
                          name="spaceId"
                          value={eventForm.spaceId}
                          onChange={handleFormChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">-- Use custom location --</option>
                          {spaces.map(space => (
                            <option key={space.id} value={space.id}>
                              {space.name} {space.city && `(${space.city})`}
                            </option>
                          ))}
                        </select>
                      </div>
                      {!eventForm.spaceId && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Custom Location
                            </label>
                            <input
                              type="text"
                              name="location"
                              value={eventForm.location}
                              onChange={handleFormChange}
                              placeholder="e.g., Community Center, 123 Main St"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Venue Link (Google Maps, etc.)
                            </label>
                            <input
                              type="url"
                              name="venueLink"
                              value={eventForm.venueLink}
                              onChange={handleFormChange}
                              placeholder="https://maps.google.com/..."
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Descriptions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Short Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="shortDescription"
                      value={eventForm.shortDescription}
                      onChange={handleFormChange}
                      placeholder="Brief overview of your event (shown in cards)"
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detailed Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="detailedDescription"
                      value={eventForm.detailedDescription}
                      onChange={handleFormChange}
                      placeholder="Full details about your event, what to expect, what to bring..."
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      required
                    />
                  </div>

                  {/* Registration & Pricing */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fee
                      </label>
                      <input
                        type="text"
                        name="fee"
                        value={eventForm.fee}
                        onChange={handleFormChange}
                        placeholder="e.g., ₹599 or 'Free'"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Attendees
                      </label>
                      <input
                        type="number"
                        name="maxAttendees"
                        value={eventForm.maxAttendees}
                        onChange={handleFormChange}
                        placeholder="Leave empty for unlimited"
                        min="1"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      External Registration Link (optional)
                    </label>
                    <input
                      type="url"
                      name="registrationLink"
                      value={eventForm.registrationLink}
                      onChange={handleFormChange}
                      placeholder="https://..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="registrationOpen"
                      checked={eventForm.registrationOpen}
                      onChange={handleFormChange}
                      id="registrationOpen"
                      className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="registrationOpen" className="text-sm text-gray-700">
                      Registration is open on the platform
                    </label>
                  </div>

                  {/* Facilitators (Space Owners Only) */}
                  {isSpaceOwner && (
                    <div className="space-y-4 p-4 bg-blue-50 rounded-xl">
                      <h3 className="font-semibold text-gray-900">Facilitators</h3>
                      <p className="text-sm text-gray-600">
                        As a space owner, you can add other facilitators to this event.
                      </p>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {facilitators.map(fac => (
                          <label
                            key={fac.profileId}
                            className="flex items-center gap-3 p-2 hover:bg-blue-100 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={eventForm.facilitatorIds.includes(fac.profileId)}
                              onChange={() => handleFacilitatorToggle(fac.profileId)}
                              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                            />
                            <span className="text-sm">
                              {fac.name} <span className="text-gray-500">({fac.email})</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* External Facilitators */}
                  <div className="space-y-4 p-4 bg-amber-50 rounded-xl">
                    <h3 className="font-semibold text-gray-900">External Facilitators</h3>
                    <p className="text-sm text-gray-600">
                      Add facilitators who are not on the platform.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={externalFacInput}
                        onChange={(e) => setExternalFacInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExternalFacilitator())}
                        placeholder="Enter facilitator name"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={handleAddExternalFacilitator}
                        className="px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    {eventForm.externalFacilitators.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {eventForm.externalFacilitators.map((name, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-sm"
                          >
                            {name}
                            <button
                              type="button"
                              onClick={() => handleRemoveExternalFacilitator(index)}
                              className="hover:text-amber-900"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Event Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Cover Image
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4">
                      {imagePreview ? (
                        <div className="relative">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-full h-48 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setImageFile(null)
                              setImagePreview(null)
                            }}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center h-32 cursor-pointer">
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Click to upload image</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Media Gallery Section */}
                  <div className="space-y-4 p-4 bg-indigo-50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">Additional Media</h3>
                      <span className="text-sm text-gray-500">
                        Images, Videos, PDFs
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Add additional media files to your event (photos, videos, PDFs). You can upload files or paste URLs (YouTube, Vimeo, etc.).
                    </p>

                    {/* Existing Media */}
                    {existingMedia.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">Current Media</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {existingMedia.map((media) => (
                            <div
                              key={media.id}
                              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
                            >
                              {media.type === 'image' && (
                                <img
                                  src={getMediaUrl(media.url)}
                                  alt={media.caption || 'Media'}
                                  className="w-full h-full object-cover"
                                />
                              )}
                              {media.type === 'video' && (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 text-white">
                                  <Film className="w-8 h-8 mb-1" />
                                  <span className="text-xs">Video</span>
                                </div>
                              )}
                              {media.type === 'pdf' && (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-red-100">
                                  <FileText className="w-8 h-8 text-red-600 mb-1" />
                                  <span className="text-xs text-red-700 px-1 text-center line-clamp-2">
                                    {getMediaDisplayName(media)}
                                  </span>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => handleMarkExistingMediaForDeletion(media.id)}
                                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                title="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* New Media Files Preview */}
                    {newMediaFiles.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">New Files to Upload</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {newMediaFiles.map((item, index) => (
                            <div
                              key={index}
                              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group border-2 border-green-300"
                            >
                              {item.type === 'image' && item.preview && (
                                <img
                                  src={item.preview}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              )}
                              {item.type === 'video' && (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 text-white">
                                  <Film className="w-8 h-8 mb-1" />
                                  <span className="text-xs px-1 text-center line-clamp-2">{item.name}</span>
                                </div>
                              )}
                              {item.type === 'pdf' && (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-red-100">
                                  <FileText className="w-8 h-8 text-red-600 mb-1" />
                                  <span className="text-xs text-red-700 px-1 text-center line-clamp-2">{item.name}</span>
                                </div>
                              )}
                              <div className="absolute bottom-0 left-0 right-0 bg-green-600 text-white text-xs py-0.5 text-center">
                                {formatFileSize(item.size)}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveNewMediaFile(index)}
                                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                title="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* New Media URLs Preview */}
                    {newMediaUrls.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">New URLs to Add</h4>
                        <div className="space-y-2">
                          {newMediaUrls.map((item, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 p-2 bg-white rounded-lg border border-green-300"
                            >
                              <span className="text-lg">
                                {item.type === 'video' && '🎬'}
                                {item.type === 'image' && '🖼️'}
                                {item.type === 'pdf' && '📄'}
                              </span>
                              <span className="flex-1 text-sm text-gray-700 truncate">{item.url}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveNewMediaUrl(index)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add Media Controls */}
                    <div className="space-y-3">
                      {/* File Upload */}
                      <div>
                        <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-indigo-300 rounded-xl cursor-pointer hover:bg-indigo-100 transition-colors">
                          <Upload className="w-5 h-5 text-indigo-600" />
                          <span className="text-sm font-medium text-indigo-600">Upload Files</span>
                          <input
                            type="file"
                            accept={getAcceptedMediaTypes()}
                            multiple
                            onChange={handleMediaFilesChange}
                            className="hidden"
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-1 text-center">
                          Images, Videos (MP4, WebM), PDFs - Max 50MB each
                        </p>
                      </div>

                      {/* URL Input */}
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="url"
                            value={mediaUrlInput}
                            onChange={(e) => setMediaUrlInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMediaUrl())}
                            placeholder="Paste URL (YouTube, Vimeo, image, PDF...)"
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddMediaUrl}
                          disabled={!mediaUrlInput.trim()}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Media Error */}
                    {mediaError && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {mediaError}
                      </div>
                    )}
                  </div>

                  {/* Post-Event Resources (only when editing) */}
                  {editingEvent && (
                    <div className="space-y-4 p-4 bg-emerald-50 rounded-xl">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">Post-Event Resources</h3>
                        <span className="text-sm text-gray-500">Recordings, PDFs, Links</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Share session recordings, handouts, and other resources with attendees after the event.
                      </p>

                      {/* Existing Resources */}
                      {postEventResources.length > 0 && (
                        <div className="space-y-2">
                          {postEventResources.map((resource) => (
                            <div key={resource.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 group">
                              <span className={`p-1.5 rounded ${getResourceBadgeColor(resource.resourceType)}`}>
                                {getResourceIcon(resource.resourceType)}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{resource.title}</p>
                                {resource.description && (
                                  <p className="text-xs text-gray-500 truncate">{resource.description}</p>
                                )}
                                <p className="text-xs text-gray-400 truncate">
                                  {resource.type === 'link' ? resource.url : resource.originalName}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleDeleteResource(resource.id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Resource Form */}
                      <div className="space-y-3 p-3 bg-white rounded-lg border border-gray-200">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setResourceForm(prev => ({ ...prev, mode: 'link' }))}
                            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                              resourceForm.mode === 'link' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            Link / URL
                          </button>
                          <button
                            type="button"
                            onClick={() => setResourceForm(prev => ({ ...prev, mode: 'file' }))}
                            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                              resourceForm.mode === 'file' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            Upload File
                          </button>
                        </div>

                        <input
                          type="text"
                          value={resourceForm.title}
                          onChange={(e) => setResourceForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Resource title"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                        />

                        {resourceForm.mode === 'link' ? (
                          <input
                            type="url"
                            value={resourceForm.url}
                            onChange={(e) => setResourceForm(prev => ({ ...prev, url: e.target.value }))}
                            placeholder="https://youtube.com/... or https://drive.google.com/..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                          />
                        ) : (
                          <div>
                            <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-emerald-300 rounded-lg cursor-pointer hover:bg-emerald-50 transition-colors">
                              <Upload className="w-4 h-4 text-emerald-600" />
                              <span className="text-sm text-emerald-600">
                                {resourceFile ? resourceFile.name : 'Choose file'}
                              </span>
                              <input
                                type="file"
                                accept="video/*,application/pdf,image/*,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                                onChange={(e) => {
                                  const file = e.target.files[0]
                                  if (file) {
                                    setResourceFile(file)
                                    if (!resourceForm.title) {
                                      setResourceForm(prev => ({ ...prev, title: file.name }))
                                    }
                                  }
                                }}
                                className="hidden"
                              />
                            </label>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={resourceForm.resourceType}
                            onChange={(e) => setResourceForm(prev => ({ ...prev, resourceType: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          >
                            <option value="recording">Recording</option>
                            <option value="pdf">PDF</option>
                            <option value="document">Document</option>
                            <option value="other">Other</option>
                          </select>
                          <input
                            type="text"
                            value={resourceForm.description}
                            onChange={(e) => setResourceForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Description (optional)"
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={handleAddResource}
                          disabled={resourceSaving}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 text-sm font-medium"
                        >
                          <Plus className="w-4 h-4" />
                          {resourceSaving ? 'Adding...' : 'Add Resource'}
                        </button>

                        {resourceError && (
                          <p className="text-red-600 text-sm">{resourceError}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {formError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                      {formError}
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Registrations Modal */}
      <AnimatePresence>
        {showRegistrationsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowRegistrationsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Event Registrations</h2>
                  {selectedEventForRegistrations && (
                    <p className="text-sm text-gray-600">{selectedEventForRegistrations.name}</p>
                  )}
                </div>
                <button
                  onClick={() => setShowRegistrationsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                {registrationsLoading ? (
                  <div className="flex justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : registrations.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No registrations yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {registrations.map((reg, index) => (
                      <RegistrationRow key={reg.id} reg={reg} index={index} />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default FacilitatorEvents

